import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, LogOut, Plus, BookOpen, Edit, Trash2, Share2, ExternalLink, FileText, Video, Link2, Search, Upload, Download, File, Bookmark, BookmarkCheck, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'glp', label: 'Gestalt Language Processing (GLP)' },
  { value: 'aac', label: 'AAC & Augmentative Communication' },
  { value: 'sensory', label: 'Sensory Processing' },
  { value: 'communication', label: 'Communication Development' },
  { value: 'behavior', label: 'Behavior & Regulation' },
  { value: 'parent_tips', label: 'Tips for Parents' },
  { value: 'play', label: 'Play-Based Learning' },
  { value: 'neurodiversity', label: 'Neurodiversity & Affirming Practices' },
  { value: 'other', label: 'Other Resources' }
];

const RESOURCE_TYPES = [
  { value: 'article', label: 'Article', icon: FileText },
  { value: 'video_link', label: 'Video Link', icon: Video },
  { value: 'external_link', label: 'External Link', icon: Link2 },
  { value: 'uploaded_file', label: 'Upload File', icon: Upload }
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/png',
  'image/jpeg',
  'image/gif'
];

export default function ResourceLibrary({ onLogout }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [resources, setResources] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [editResource, setEditResource] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sharedResources, setSharedResources] = useState({});
  const [bookmarks, setBookmarks] = useState(new Set());
  const [showBookmarksOnly, setShowBookmarksOnly] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    category: 'communication',
    tags: '',
    resource_type: 'article',
    external_url: ''
  });

  useEffect(() => {
    fetchResources();
    if (user.role === 'therapist') {
      fetchChildren();
      fetchBookmarks();
    }
  }, [filterCategory]);

  const fetchBookmarks = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/me/resource-bookmarks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const ids = new Set((res.data?.bookmarks || []).map((b) => b.resource_id));
      setBookmarks(ids);
    } catch (e) {
      // silent
    }
  };

  const toggleBookmark = async (resourceId) => {
    const wasBookmarked = bookmarks.has(resourceId);
    const next = new Set(bookmarks);
    if (wasBookmarked) next.delete(resourceId);
    else next.add(resourceId);
    setBookmarks(next);
    try {
      const token = localStorage.getItem('token');
      if (wasBookmarked) {
        await axios.delete(`${API}/me/resource-bookmarks/${resourceId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Removed from saved');
      } else {
        await axios.post(
          `${API}/me/resource-bookmarks/${resourceId}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success('Saved to your library');
      }
    } catch (e) {
      // revert
      setBookmarks(bookmarks);
      toast.error('Failed to update saved status');
    }
  };

  const fetchResources = async (retryCount = 0) => {
    try {
      const token = localStorage.getItem('token');
      const params = filterCategory !== 'all' ? `?category=${filterCategory}` : '';
      const response = await axios.get(`${API}/resources${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setResources(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Auto-retry once on transient errors (network blip, cold start, 502/503)
      // before showing the user a toast. Massively reduces the "Failed to load"
      // false positives colleagues were seeing.
      const status = error.response?.status;
      const transient = !status || status >= 500 || error.code === 'ECONNABORTED';
      if (transient && retryCount < 1) {
        await new Promise((r) => setTimeout(r, 800));
        return fetchResources(retryCount + 1);
      }
      // 401 → don't show a misleading "Failed to load" toast; the
      // ProtectedRoute will bounce them to login.
      if (status !== 401) {
        toast.error('Failed to load resources');
      }
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchChildren = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/children`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildren(response.data);
      
      // Fetch shared resources for each child
      const sharedMap = {};
      for (const child of response.data) {
        const sharedRes = await axios.get(`${API}/resources/shared/${child.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        sharedMap[child.id] = sharedRes.data.map(r => r.id);
      }
      setSharedResources(sharedMap);
    } catch (error) {
      console.error('Failed to load children');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate file upload if type is uploaded_file
    if (formData.resource_type === 'uploaded_file' && !uploadedFile && !editResource?.file_data) {
      toast.error('Please select a file to upload');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
      };
      
      // Add file data if uploading
      if (formData.resource_type === 'uploaded_file' && uploadedFile) {
        payload.file_name = uploadedFile.name;
        payload.file_type = uploadedFile.type;
        payload.file_size = uploadedFile.size;
        payload.file_data = uploadedFile.data;
      }

      if (editResource) {
        await axios.put(`${API}/resources/${editResource.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Resource updated!');
      } else {
        await axios.post(`${API}/resources`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Resource created!');
      }

      setShowCreateDialog(false);
      setEditResource(null);
      resetForm();
      fetchResources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save resource');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      toast.error('Unsupported file type. Please upload PDF, Word, PowerPoint, or image files.');
      return;
    }
    
    // Read file as base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:xxx;base64, prefix
      setUploadedFile({
        name: file.name,
        type: file.type,
        size: file.size,
        data: base64
      });
      toast.success(`File "${file.name}" selected`);
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  const handleDownload = async (resource) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/resources/${resource.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create blob with proper MIME type
      const contentType = response.headers['content-type'] || resource.file_type || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const filename = resource.file_name || 'download';

      if (isIOS) {
        // iOS Safari: a hidden <a download> is much more reliable than
        // window.open for triggering the "Save to Files / Open in..." sheet.
        // Falling back to window.open ONLY if the click was blocked.
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        link.setAttribute('target', '_blank');
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Downloaded — check Files or your share sheet.');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Download started');
      }

      // Cleanup after delay
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  // Open the file inline in a new tab (preview) — works for PDFs and images.
  const handlePreview = async (resource) => {
    if (resource.resource_type !== 'uploaded_file') {
      toast.error('Preview only available for uploaded files');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/resources/${resource.id}/download?inline=true`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
      );
      const contentType = response.headers['content-type'] || resource.file_type || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Failed to preview file');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleEdit = (resource) => {
    setEditResource(resource);
    setFormData({
      title: resource.title,
      content: resource.content,
      summary: resource.summary || '',
      category: resource.category,
      tags: resource.tags?.join(', ') || '',
      resource_type: resource.resource_type,
      external_url: resource.external_url || ''
    });
    // Set uploaded file info if editing a file resource
    if (resource.resource_type === 'uploaded_file' && resource.file_name) {
      setUploadedFile({
        name: resource.file_name,
        type: resource.file_type,
        size: resource.file_size,
        data: null // Don't re-upload unless changed
      });
    }
    setShowCreateDialog(true);
  };

  const handleDelete = async (id) => {
    const resource = resources.find(r => r.id === id);
    const isOwnResource = resource?.author_id === user.id;
    
    const confirmMessage = isOwnResource 
      ? 'Delete this resource?' 
      : `Delete this resource by ${resource?.author_name || 'another therapist'}? (Super Admin action)`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/resources/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resource deleted');
      fetchResources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete resource');
    }
  };

  const handleShare = async (resourceId, childId) => {
    try {
      const token = localStorage.getItem('token');
      const isShared = sharedResources[childId]?.includes(resourceId);
      
      if (isShared) {
        await axios.delete(`${API}/resources/share/${childId}/${resourceId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Resource unshared');
      } else {
        await axios.post(`${API}/resources/share`, 
          { resource_id: resourceId, child_id: childId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Resource shared with parent!');
      }
      
      fetchChildren();
    } catch (error) {
      toast.error('Failed to update share');
    }
  };

  // Inline invite-code helper used by the share dialog when a child has
  // 0 parents connected. Lets the therapist generate + copy the code
  // without leaving the dialog.
  const [inviteForChild, setInviteForChild] = useState({}); // {child_id: code}
  const [invitingChildId, setInvitingChildId] = useState(null);
  const showInviteForChild = async (child) => {
    if (child.invite_code) {
      setInviteForChild((m) => ({ ...m, [child.id]: child.invite_code }));
      try { await navigator.clipboard.writeText(child.invite_code); toast.success('Invite code copied'); } catch { /* noop */ }
      return;
    }
    setInvitingChildId(child.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/children/${child.id}/invite-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const code = res.data?.invite_code;
      if (code) {
        setInviteForChild((m) => ({ ...m, [child.id]: code }));
        try { await navigator.clipboard.writeText(code); toast.success('Invite code copied'); } catch { /* noop */ }
      }
    } catch {
      toast.error("Couldn't generate invite code");
    } finally {
      setInvitingChildId(null);
    }
  };

  const openShareDialog = (resource) => {
    setSelectedResource(resource);
    setShowShareDialog(true);
  };

  const openViewDialog = (resource) => {
    setSelectedResource(resource);
    setShowViewDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      summary: '',
      category: 'communication',
      tags: '',
      resource_type: 'article',
      external_url: ''
    });
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getTypeIcon = (type) => {
    if (type === 'uploaded_file') return <File className="w-4 h-4" />;
    const TypeIcon = RESOURCE_TYPES.find(t => t.value === type)?.icon || FileText;
    return <TypeIcon className="w-4 h-4" />;
  };

  const getCategoryLabel = (value) => {
    return CATEGORIES.find(c => c.value === value)?.label || value;
  };

  const filteredResources = resources.filter(r => {
    if (!r) return false;
    if (showBookmarksOnly && !bookmarks.has(r.id)) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (r.title || '').toLowerCase().includes(query)
      || (r.summary || '').toLowerCase().includes(query)
      || (Array.isArray(r.tags) && r.tags.some((t) => (t || '').toLowerCase().includes(query)))
    );
  });

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
          <Button variant="outline" data-testid="logout-btn" onClick={onLogout} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Community Sharing Banner - only show for therapists */}
          {user.role === 'therapist' && (
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-4 mb-6 border border-emerald-100" data-testid="community-banner">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-emerald-900 mb-1">Growing Together</h3>
                  <p className="text-sm text-emerald-700">
                    Every resource you add helps fellow therapists and the families they serve! 
                    Share articles, videos, handouts, and educational materials. 
                    Together, we're building a comprehensive knowledge base for GLP-informed, neurodiversity-affirming practice.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-2">
                Learn More
              </h2>
              <p className="text-lg text-stone-600">
                {user.role === 'therapist' 
                  ? 'Educational resources for therapists and families'
                  : 'Resources shared by your therapist'}
              </p>
            </div>
            {user.role === 'therapist' && (
              <Dialog
                open={showCreateDialog}
                onOpenChange={(open) => {
                  setShowCreateDialog(open);
                  if (!open) {
                    setEditResource(null);
                    resetForm();
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button data-testid="add-resource-btn" className="rounded-full shadow-lg shadow-primary/20">
                    <Plus className="w-4 h-4 mr-2" />
                    Add resource
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="resource-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif">
                      {editResource ? 'Edit resource' : 'Add resource'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        data-testid="resource-title-input"
                        placeholder="Understanding Gestalt Language Processing"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                        className="h-12 rounded-2xl"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select
                          value={formData.category}
                          onValueChange={(value) => setFormData({ ...formData, category: value })}
                        >
                          <SelectTrigger className="h-12 rounded-2xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map(cat => (
                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Type</Label>
                        <Select
                          value={formData.resource_type}
                          onValueChange={(value) => setFormData({ ...formData, resource_type: value })}
                        >
                          <SelectTrigger className="h-12 rounded-2xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {RESOURCE_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="summary">Summary</Label>
                      <Textarea
                        id="summary"
                        data-testid="resource-summary-input"
                        placeholder="Brief description of this resource..."
                        value={formData.summary}
                        onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                        className="rounded-2xl min-h-16"
                      />
                    </div>

                    {formData.resource_type === 'uploaded_file' && (
                      <div className="space-y-2">
                        <Label>Upload File</Label>
                        <div className="border-2 border-dashed border-stone-200 rounded-2xl p-6 text-center hover:border-primary/50 transition-colors">
                          <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleFileSelect}
                            accept=".pdf,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg,.gif"
                            className="hidden"
                            data-testid="file-input"
                          />
                          {uploadedFile ? (
                            <div className="space-y-2">
                              <File className="w-10 h-10 mx-auto text-primary" />
                              <p className="font-medium text-primary-900">{uploadedFile.name}</p>
                              <p className="text-sm text-stone-500">{formatFileSize(uploadedFile.size)}</p>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="rounded-full"
                                onClick={() => fileInputRef.current?.click()}
                              >
                                Change file
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Upload className="w-10 h-10 mx-auto text-stone-400" />
                              <p className="text-stone-600">Click to select a file</p>
                              <p className="text-xs text-stone-400">PDF, Word, PowerPoint, or images up to 10MB</p>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="rounded-full"
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="select-file-btn"
                              >
                                Select file
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {(formData.resource_type === 'video_link' || formData.resource_type === 'external_link') && (
                      <div className="space-y-2">
                        <Label htmlFor="external_url">External URL</Label>
                        <Input
                          id="external_url"
                          type="url"
                          data-testid="resource-url-input"
                          placeholder="https://..."
                          value={formData.external_url}
                          onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
                          className="h-12 rounded-2xl"
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="content">
                        {formData.resource_type === 'article' ? 'Content' : 'Notes / Description'}
                      </Label>
                      <Textarea
                        id="content"
                        data-testid="resource-content-input"
                        placeholder={formData.resource_type === 'article' 
                          ? "Write your article content here... (Markdown supported)"
                          : "Additional notes about this resource..."
                        }
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        required
                        className="rounded-2xl min-h-40"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tags">Tags (comma-separated)</Label>
                      <Input
                        id="tags"
                        data-testid="resource-tags-input"
                        placeholder="glp, stage 1, echolalia"
                        value={formData.tags}
                        onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                        className="h-12 rounded-2xl"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      data-testid="save-resource-btn" 
                      className="w-full h-12 rounded-full"
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : editResource ? 'Save changes' : 'Create resource'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 rounded-2xl"
                data-testid="search-input"
              />
            </div>
            {user.role === 'therapist' && (
              <Button
                variant={showBookmarksOnly ? 'default' : 'outline'}
                onClick={() => setShowBookmarksOnly((v) => !v)}
                className="h-12 rounded-2xl"
                data-testid="toggle-bookmarks-only-btn"
              >
                {showBookmarksOnly ? (
                  <BookmarkCheck className="w-4 h-4 mr-2" />
                ) : (
                  <Bookmark className="w-4 h-4 mr-2" />
                )}
                {showBookmarksOnly ? `Saved (${bookmarks.size})` : 'My Saved'}
              </Button>
            )}
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-full md:w-64 h-12 rounded-2xl" data-testid="filter-category">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-stone-600">Loading...</p>
            </div>
          ) : filteredResources.length === 0 ? (
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100" data-testid="empty-state">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-primary/50" />
              <h3 className="text-xl font-medium text-stone-700 mb-2">
                {showBookmarksOnly ? 'No saved resources yet' : 'No resources yet'}
              </h3>
              <p className="text-stone-500">
                {showBookmarksOnly
                  ? 'Tap the bookmark icon on any resource to save it for later.'
                  : user.role === 'therapist'
                    ? 'Add educational resources for yourself and parents'
                    : 'Your therapist will share resources with you here'}
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResources.map((resource, index) => (
                <motion.div
                  key={resource.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <Card 
                    className="p-6 bg-white rounded-3xl border border-stone-100 hover:shadow-md transition-all cursor-pointer h-full flex flex-col"
                    data-testid={`resource-card-${resource.id}`}
                    onClick={() => openViewDialog(resource)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          {getTypeIcon(resource.resource_type)}
                        </div>
                        <span className="text-xs font-medium text-primary px-2 py-0.5 bg-primary/10 rounded-full">
                          {getCategoryLabel(resource.category)}
                        </span>
                      </div>
                      {user.role === 'therapist' && (
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`bookmark-resource-${resource.id}`}
                            onClick={() => toggleBookmark(resource.id)}
                            className={`rounded-full h-8 w-8 p-0 ${
                              bookmarks.has(resource.id) ? 'text-amber-600' : 'text-stone-400 hover:text-amber-600'
                            }`}
                            title={bookmarks.has(resource.id) ? 'Remove from saved' : 'Save for later'}
                          >
                            {bookmarks.has(resource.id) ? (
                              <BookmarkCheck className="w-4 h-4" />
                            ) : (
                              <Bookmark className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`share-resource-${resource.id}`}
                            onClick={() => openShareDialog(resource)}
                            className="rounded-full h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                          >
                            <Share2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`edit-resource-${resource.id}`}
                            onClick={() => handleEdit(resource)}
                            className="rounded-full h-8 w-8 p-0"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {(resource.author_id === user.id || user.is_super_admin) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`delete-resource-${resource.id}`}
                              onClick={() => handleDelete(resource.id)}
                              className="rounded-full h-8 w-8 p-0 text-destructive hover:text-destructive"
                              title={resource.author_id !== user.id ? `Delete (by ${resource.author_name})` : 'Delete'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    <h3 className="text-lg font-serif text-primary-900 mb-2 line-clamp-2">{resource.title}</h3>

                    {user.role === 'therapist' && (() => {
                      // "Sent before" indicator — counts how many distinct
                      // children this resource has been shared with. Mirrors
                      // the Activity Library tracking pattern.
                      const sharedWith = Object.values(sharedResources).filter(
                        (idList) => Array.isArray(idList) && idList.includes(resource.id),
                      ).length;
                      if (sharedWith === 0) return null;
                      return (
                        <div className="mb-2" data-testid={`resource-shared-badge-${resource.id}`}>
                          <span className="text-xs font-medium text-emerald-700 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full inline-flex items-center gap-1">
                            <Share2 className="w-3 h-3" />
                            Sent to {sharedWith} {sharedWith === 1 ? 'child' : 'children'}
                          </span>
                        </div>
                      );
                    })()}

                    {resource.summary && (
                      <p className="text-sm text-stone-600 mb-3 line-clamp-2 flex-1">{resource.summary}</p>
                    )}

                    {resource.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-auto">
                        {resource.tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-stone-100 text-stone-600 rounded-full">
                            {tag}
                          </span>
                        ))}
                        {resource.tags.length > 3 && (
                          <span className="text-xs text-stone-400">+{resource.tags.length - 3}</span>
                        )}
                      </div>
                    )}

                    {resource.external_url && (
                      <div className="mt-3 pt-3 border-t border-stone-100">
                        <a
                          href={resource.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open link
                        </a>
                      </div>
                    )}

                    {resource.resource_type === 'uploaded_file' && resource.file_name && (
                      <div className="mt-3 pt-3 border-t border-stone-100">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(resource);
                          }}
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          <Download className="w-3 h-3" />
                          Download {resource.file_name}
                        </button>
                        {resource.file_size && (
                          <span className="text-xs text-stone-400 ml-2">
                            ({formatFileSize(resource.file_size)})
                          </span>
                        )}
                      </div>
                    )}
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      {/* View Resource Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-resource-dialog">
          {selectedResource && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    {getTypeIcon(selectedResource.resource_type)}
                  </div>
                  <span className="text-xs font-medium text-primary px-2 py-0.5 bg-primary/10 rounded-full">
                    {getCategoryLabel(selectedResource.category)}
                  </span>
                  {user.role === 'therapist' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleBookmark(selectedResource.id)}
                      className={`rounded-full h-8 w-8 p-0 ml-auto ${
                        bookmarks.has(selectedResource.id) ? 'text-amber-600' : 'text-stone-400 hover:text-amber-600'
                      }`}
                      data-testid="view-dialog-bookmark-btn"
                      title={bookmarks.has(selectedResource.id) ? 'Remove from saved' : 'Save for later'}
                    >
                      {bookmarks.has(selectedResource.id) ? (
                        <BookmarkCheck className="w-5 h-5" />
                      ) : (
                        <Bookmark className="w-5 h-5" />
                      )}
                    </Button>
                  )}
                </div>
                <DialogTitle className="text-2xl font-serif">{selectedResource.title}</DialogTitle>
                {selectedResource.author_name && (
                  <p className="text-sm text-stone-500">By {selectedResource.author_name}</p>
                )}
              </DialogHeader>
              <div className="mt-4 space-y-4">
                {selectedResource.summary && (
                  <p className="text-stone-600 italic">{selectedResource.summary}</p>
                )}
                
                {selectedResource.external_url && (
                  <a
                    href={selectedResource.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full hover:bg-primary/20 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open {selectedResource.resource_type === 'video_link' ? 'Video' : 'Link'}
                  </a>
                )}

                {selectedResource.resource_type === 'uploaded_file' && selectedResource.file_name && (() => {
                  const ftype = (selectedResource.file_type || '').toLowerCase();
                  const isPreviewable = ftype.startsWith('image/') || ftype === 'application/pdf';
                  return (
                    <div className="flex flex-wrap gap-2">
                      {isPreviewable && (
                        <button
                          onClick={() => handlePreview(selectedResource)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 text-stone-700 rounded-full hover:bg-stone-200 transition-colors"
                          data-testid="resource-preview-btn"
                        >
                          <FileText className="w-4 h-4" />
                          Preview
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(selectedResource)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full hover:bg-primary/20 transition-colors"
                        data-testid="resource-download-btn"
                      >
                        <Download className="w-4 h-4" />
                        Download {selectedResource.file_name}
                        {selectedResource.file_size && (
                          <span className="text-xs opacity-70">
                            ({formatFileSize(selectedResource.file_size)})
                          </span>
                        )}
                      </button>
                    </div>
                  );
                })()}

                <div className="prose prose-stone max-w-none">
                  <div className="whitespace-pre-wrap text-stone-700 leading-relaxed">
                    {selectedResource.content}
                  </div>
                </div>

                {selectedResource.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-stone-100">
                    {selectedResource.tags.map((tag, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-stone-100 text-stone-600 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Resource Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="share-resource-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Share with Parents</DialogTitle>
          </DialogHeader>
          {selectedResource && (
            <div className="mt-4 space-y-4">
              <p className="text-sm text-stone-600">
                Select which children&apos;s parents should see &quot;{selectedResource.title}&quot;
              </p>
              
              {children.length === 0 ? (
                <p className="text-sm text-stone-500 text-center py-4">
                  No children added yet
                </p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {children.map(child => {
                    const isShared = sharedResources[child.id]?.includes(selectedResource.id);
                    const parentCount = child.parent_ids?.length || 0;
                    const hasParents = parentCount > 0;
                    const surfacedCode = inviteForChild[child.id];
                    return (
                      <div
                        key={child.id}
                        className={`w-full p-3 rounded-xl transition-colors ${
                          isShared
                            ? 'bg-green-50 border-2 border-green-200'
                            : hasParents
                              ? 'bg-stone-50 border-2 border-transparent hover:border-stone-200'
                              : 'bg-amber-50/60 border-2 border-amber-200'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => hasParents && handleShare(selectedResource.id, child.id)}
                          disabled={!hasParents}
                          className="w-full text-left flex items-center justify-between disabled:cursor-default"
                          data-testid={`share-row-${child.id}`}
                        >
                          <div>
                            <p className="font-medium text-primary-900">{child.name}</p>
                            <p className={`text-xs mt-0.5 ${hasParents ? 'text-stone-500' : 'text-amber-800'}`}>
                              {hasParents
                                ? `${parentCount} parent${parentCount === 1 ? '' : 's'} connected`
                                : 'No parent connected yet — they need this child\u2019s invite code'}
                            </p>
                          </div>
                          {isShared && (
                            <span className="text-xs font-medium text-green-700 px-2 py-1 bg-green-100 rounded-full">
                              Shared
                            </span>
                          )}
                        </button>
                        {!hasParents && (
                          <div className="mt-2 pt-2 border-t border-amber-200/70">
                            {surfacedCode ? (
                              <div className="flex items-center gap-2">
                                <code className="flex-1 bg-white border border-amber-200 px-3 py-2 rounded-lg text-sm font-mono tracking-widest text-amber-900 text-center">
                                  {surfacedCode}
                                </code>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="rounded-full text-amber-800 border-amber-300"
                                  onClick={() => {
                                    navigator.clipboard.writeText(surfacedCode);
                                    toast.success('Invite code copied');
                                  }}
                                  data-testid={`copy-invite-${child.id}`}
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={invitingChildId === child.id}
                                onClick={() => showInviteForChild(child)}
                                className="rounded-full text-amber-800 border-amber-300 hover:bg-amber-100/60 w-full"
                                data-testid={`gen-invite-${child.id}`}
                              >
                                {invitingChildId === child.id ? 'Generating…' : 'Show invite code for parent'}
                              </Button>
                            )}
                            <p className="text-[11px] text-amber-700/80 mt-1.5 leading-snug">
                              Share this code with the parent. They'll tap <em>“I have an invite code”</em> on PlayFirst and enter it — once connected, you can share resources with them.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => setShowShareDialog(false)}
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
